// "Für den nächsten Bezug": Claude's proposal as old → new (FR-31) and one button. The button shows the spinner until
// the machine has answered the one set command; the values in the Bezug tab stay dimmed until it confirms them (the
// store's provisional values). No success toast: the button turns into "Übernommen"; a failure is said by the
// command toast in plain words, as for every other change.
import { useState } from 'react';
import type { CommandOutcome } from '../../core/index.js';
import type { ApplyView } from '../../ai/view.js';
import { Button } from '../components/index.js';
import { useMachine } from '../machine-context.js';

/**
 * Adopting a proposal: while its command is out, the proposal stays on screen as it was (the provisional values
 * would otherwise make it "nothing to change" and the box would vanish); done marks it adopted.
 */
export function useApply() {
  const { send } = useMachine();
  const [sending, setSending] = useState<ApplyView | null>(null);
  const apply = async (
    view: ApplyView,
    onDone: (view: ApplyView) => void,
  ): Promise<CommandOutcome | null> => {
    if (!view.command || sending) return null;
    setSending(view);
    const outcome = await send(view.command);
    if (outcome.result === 'done') onDone(view);
    setSending(null);
    return outcome;
  };
  return { sending, apply };
}

export function ApplyBox({
  view,
  sending,
  onApply,
  testId,
}: {
  view: ApplyView;
  /** its command is out (the button carries the spinner, the values are dimmed) */
  sending: boolean;
  onApply: () => void;
  testId: string;
}) {
  const { ctx } = useMachine();
  return (
    <div className={sending ? 'ai-apply is-sending' : 'ai-apply'} data-testid={testId}>
      <span className="label">{ctx.tr('aiForNext')}</span>
      <ul className="ai-changes">
        {view.rows.map((r) => (
          <li key={r}>{r}</li>
        ))}
      </ul>
      {view.hint && <p className="hint">{view.hint}</p>}
      <Button
        variant={view.applied ? 'secondary' : 'primary'}
        size="sm"
        icon={view.applied ? 'check' : undefined}
        busy={sending}
        disabled={!view.enabled || sending}
        onClick={onApply}
        data-testid={`${testId}-go`}
      >
        {view.label}
      </Button>
    </div>
  );
}
